import { defineStore, acceptHMRUpdate } from 'pinia';
import chunk from 'lodash/chunk';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import set from 'lodash/set';
import { isJiraConnected, getAllJiraProjects } from '@/vuex/modules/voc-items/voc-items.utils';
import {
    createJiraIssue as createJiraIssueApi,
    getJiraServerInfo,
    loadAllJiraIssuesForVocItem,
    unlinkJiraIssue as unlinkJiraIssueApi,
    linkJiraIssueToVocItem,
    getJiraFields,
    getJiraInstances,
    getJiraIssueDetails,
} from '@/voc/api/jira.api';
import { useVocItemsStore } from '@/stores/voc-items.store';

import { useProductLinesStore } from '@/stores/product-lines.store';

const NUMERIC_JIRA_ID = /^[1-9]\d*$/;
const JIRA_ISSUE_DETAILS_BATCH_SIZE = 50;
const ISSUE_TYPE_ICON_BATCH_DELAY_MS = 50;

let pendingIssueTypeIconIds = new Set<string>();
let flushIssueTypeIcons: (() => void) | null = null;

interface JiraIssueDetails {
    id?: string;
    fields?: { issuetype?: { iconUrl?: string } };
}

interface VocItemsJiraState {
    fetching: boolean;
    error: unknown;
    issueTypeIconsByIssueId: Record<string, string>;
    serverInfo: Record<string, unknown>;
    jiraProjects: unknown[];
    jiraIssues: Record<string, unknown[]>;
    fields: Record<string, Record<string, unknown[]>>;
    instances: unknown[] | null;
    promises: Record<string, unknown>;
}

function getInitialState (): VocItemsJiraState {
    return {
        fetching: false,
        error: null,
        issueTypeIconsByIssueId: {},
        serverInfo: {},
        jiraProjects: [],
        jiraIssues: {},
        fields: {},
        instances: null,
        promises: {
            loadServerInfo: null,
            loadJiraIssueDetailsForVocItem: null,
            loadInstances: null,
        },
    };
}

export const useVocItemsJiraStore = defineStore('vocItemsJira', {
    state: getInitialState,
    getters: {
        shouldShowJiraIntegration (): boolean {
            const vocProductLine = useProductLinesStore().productLineByType('voc');

            return !!vocProductLine?.metadata?.integrations?.jira?.enabled;
        },
        jiraIssuesForVocItem (state): (vocItemId: string) => unknown[] | undefined {
            return (vocItemId) => state.jiraIssues[vocItemId];
        },
        instance (state): Record<string, unknown> {
            return (state.instances?.[0] as Record<string, unknown>) || {};
        },
    },
    actions: {
        setFetching ({ isFetching }: { isFetching: boolean }) {
            this.fetching = isFetching;
        },
        setError ({ error }: { error: unknown }) {
            if (error) {
                let errorMessage: unknown = error;
                const errorObj = error as Record<string, unknown>;
                const responseData = (errorObj.response as Record<string, unknown>)?.data;
                if (responseData && typeof responseData === 'string') {
                    errorMessage = responseData.trim();
                } else if (errorObj.message && typeof errorObj.message === 'string') {
                    errorMessage = errorObj.message.trim();
                }
                this.error = errorMessage;
            } else {
                this.error = error;
            }
        },
        setServerInfo ({ serverInfo }: { serverInfo: unknown }) {
            this.serverInfo = serverInfo as Record<string, unknown>;
        },
        setJiraProjects ({ jiraProjects }: { jiraProjects: unknown[] }) {
            this.jiraProjects = jiraProjects;
        },
        setJiraIssues ({ jiraIssues }: { jiraIssues: Record<string, unknown[]> }) {
            this.jiraIssues = jiraIssues;
        },
        setPromise ({ promise, key }: { promise: unknown; key: string | string[] }) {
            set(this.promises, key, promise);
        },
        setFields ({
            projectId,
            issueTypeId,
            fields,
        }: {
            projectId: string;
            issueTypeId: string;
            fields: unknown[];
        }) {
            if (!this.fields[projectId]) {
                this.fields[projectId] = {};
            }
            this.fields[projectId][issueTypeId] = fields;
        },
        setInstances ({ instances }: { instances: unknown[] }) {
            this.instances = instances;
        },
        resetState () {
            this.fetching = false;
            this.error = null;
            this.serverInfo = {};
            this.issueTypeIconsByIssueId = {};
            flushIssueTypeIcons = null;
            pendingIssueTypeIconIds = new Set();
            this.jiraProjects = [];
            this.jiraIssues = {};
            this.fields = {};
            this.promises = {
                loadServerInfo: null,
                loadJiraIssueDetailsForVocItem: null,
                loadInstances: null,
            };
            // instances intentionally not reset
        },
        async getJiraProjects ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (!isEmpty(this.jiraProjects) && !reFetch) {
                return;
            }
            try {
                // preserved bug: original passes { fetching } not { isFetching }, so this.fetching never toggles
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                this.setFetching({ fetching: true } as any);
                const jiraProjects = await getAllJiraProjects();
                this.setJiraProjects({ jiraProjects });
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                this.setFetching({ fetching: false } as any);
            }
        },
        async createJiraIssue ({ fields }: { fields: Record<string, unknown> }) {
            try {
                await this.loadServerInfo();
                const vocItemId = useVocItemsStore().bladeActiveItem?.id;
                const { isPrimary, ...formFields } = fields;
                const { id: jiraIssueId } = await createJiraIssueApi({
                    fields: formFields,
                    vocItemId,
                    isPrimary,
                });

                return jiraIssueId;
            } catch (error) {
                this.setError({ error });
                throw error;
            }
        },
        queueIssueTypeIcons ({ issueIds }: { issueIds: string[] }) {
            issueIds.forEach((issueId) => pendingIssueTypeIconIds.add(issueId));

            if (!flushIssueTypeIcons) {
                flushIssueTypeIcons = debounce(() => {
                    const idsToLoad = [...pendingIssueTypeIconIds];
                    pendingIssueTypeIconIds = new Set();

                    this.loadIssueTypeIcons({ issueIds: idsToLoad });
                }, ISSUE_TYPE_ICON_BATCH_DELAY_MS);
            }

            flushIssueTypeIcons();
        },
        async loadIssueTypeIcons ({ issueIds }: { issueIds: string[] }) {
            const idsToLoad = [...new Set(issueIds)].filter(
                (issueId) => NUMERIC_JIRA_ID.test(issueId) && !(issueId in this.issueTypeIconsByIssueId),
            );

            if (!idsToLoad.length) {
                return;
            }

            await Promise.all(
                chunk(idsToLoad, JIRA_ISSUE_DETAILS_BATCH_SIZE).map(async (issues: string[]) => {
                    try {
                        const response = await getJiraIssueDetails({ issues });
                        const icons: Record<string, string> = Object.fromEntries(issues.map((id) => [id, '']));

                        ((response?.issues || []) as JiraIssueDetails[]).forEach(({ id, fields }) => {
                            if (id && fields?.issuetype?.iconUrl) {
                                icons[id] = fields.issuetype.iconUrl;
                            }
                        });

                        this.issueTypeIconsByIssueId = { ...this.issueTypeIconsByIssueId, ...icons };
                    } catch {
                        // tags fall back to the Jira logo when the issue type is unknown
                    }
                }),
            );
        },
        async loadJiraIssueDetailsForVocItem ({
            reFetch = false,
            vocItemId,
        }: {
            reFetch?: boolean;
            vocItemId: string;
        }) {
            try {
                await this.loadServerInfo();
            } catch (error) {
                this.setError({ error });
                throw error;
            }

            const pendingPromises = this.promises.loadJiraIssueDetailsForVocItem as Record<string, unknown> | null;
            if (pendingPromises?.[vocItemId]) {
                return pendingPromises[vocItemId];
            }

            if (this.jiraIssues[vocItemId] && !reFetch) {
                return Promise.resolve();
            }

            const foundJiraIds: string[] = [];

            const promise = loadAllJiraIssuesForVocItem(vocItemId)
                .then((response: Record<string, unknown[]>) => {
                    const jiraIssues = response.issues.map((rawIssue: unknown) => {
                        const jiraIssue = rawIssue as Record<string, unknown>;
                        foundJiraIds.push(jiraIssue.id as string);

                        if (!jiraIssue.fields) {
                            return {
                                ...jiraIssue,
                                error: jiraIssue.error || { message: 'jiraIssueId not found' },
                            };
                        }

                        const serverInfo = this.serverInfo as { baseUrl?: string };

                        return {
                            ...jiraIssue,
                            ...(serverInfo.baseUrl ? {
                                url: `${serverInfo.baseUrl}/browse/${jiraIssue.key}`,
                            } : {}),
                            ...((jiraIssue.fields as Record<string, unknown>)?.parent ? {
                                parentUrl: `${serverInfo.baseUrl}/browse/${(jiraIssue.fields as Record<string, Record<string, unknown>>).parent.key}`,
                            } : {}),
                        };
                    });

                    this.setJiraIssues({
                        jiraIssues: {
                            ...this.jiraIssues,
                            [vocItemId]: jiraIssues,
                        },
                    });
                })
                .catch((error: unknown) => {
                    this.setError({ error });
                    this.setJiraIssues({
                        jiraIssues: {
                            ...this.jiraIssues,
                            [vocItemId]: [],
                        },
                    });

                    return {
                        id: vocItemId,
                        error: {
                            message:
                                (error as Record<string, Record<string, string>>).response?.data ||
                                (error as Record<string, string>).message,
                        },
                    };
                })
                .finally(() => {
                    this.setPromise({ promise: null, key: ['loadJiraIssueDetailsForVocItem', vocItemId] });
                });

            this.setPromise({ promise, key: ['loadJiraIssueDetailsForVocItem', vocItemId] });

            return promise;
        },
        async loadServerInfo ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (this.promises.loadServerInfo) {
                return this.promises.loadServerInfo as Promise<unknown>;
            }

            if (!isEmpty(this.serverInfo) && !reFetch) {
                return Promise.resolve();
            }

            if (this.error && !isJiraConnected(this.error)) {
                throw new Error(String(this.error));
            }

            const promise = getJiraServerInfo()
                .then((serverInfo: unknown) => {
                    this.setServerInfo({ serverInfo });
                })
                .catch((error: unknown) => {
                    throw error;
                })
                .finally(() => {
                    this.setPromise({ promise: null, key: 'loadServerInfo' });
                });

            this.setPromise({ promise, key: 'loadServerInfo' });

            return promise;
        },
        unlinkJiraIssue ({ vocItemId, jiraIssueId }: { vocItemId: string; jiraIssueId: string }) {
            return unlinkJiraIssueApi({ vocItemId, jiraIssueId })
                .then(() => {
                    const jiraIssues = this.jiraIssues[vocItemId].filter(
                        (jiraIssue) => (jiraIssue as Record<string, unknown>).id !== jiraIssueId,
                    );
                    const vocItemsStore = useVocItemsStore();
                    const mapItem = vocItemsStore.map[vocItemId] as Record<string, unknown>;
                    const bladeItem = (vocItemsStore.blade as unknown as Record<string, Record<string, unknown>>)
                        .activeItems[mapItem?.type as string] as Record<string, unknown>;

                    this.setJiraIssues({
                        jiraIssues: {
                            ...this.jiraIssues,
                            [vocItemId]: jiraIssues,
                        },
                    });
                    vocItemsStore.updateItem({
                        item: {
                            ...bladeItem,
                            ...mapItem,
                            jiraIssueIds: (mapItem?.jiraIssueIds as string[])?.filter(
                                (id) => id !== jiraIssueId,
                            ),
                        },
                    });
                })
                .catch((error: unknown) => {
                    this.setError({ error });
                    throw error;
                });
        },
        async linkJiraIssuesToVocItem ({
            vocItemId,
            jiraIssueIdsAndKeys,
        }: {
            vocItemId: string;
            jiraIssueIdsAndKeys: { id: string; key: string }[];
        }) {
            const serverInfo = this.serverInfo as { baseUrl?: string };
            const finalIssues = await linkJiraIssueToVocItem({ vocItemId, issues: jiraIssueIdsAndKeys })
                .then((jiraIssues: Record<string, unknown>[]) => {
                    return jiraIssues.map((jiraIssue) => {
                        return {
                            ...jiraIssue,
                            ...(serverInfo.baseUrl ? {
                                url: `${serverInfo.baseUrl}/browse/${jiraIssue.key}`,
                            } : {}),
                            ...((jiraIssue.fields as Record<string, unknown>)?.parent ? {
                                parentUrl: `${serverInfo.baseUrl}/browse/${(jiraIssue.fields as Record<string, Record<string, unknown>>).parent.key}`,
                            } : {}),
                        };
                    });
                })
                .catch((error: unknown) => {
                    this.setError({ error });
                    throw error;
                });

            this.setJiraIssues({
                jiraIssues: {
                    ...this.jiraIssues,
                    [vocItemId]: [...(this.jiraIssues[vocItemId] || []), ...finalIssues],
                },
            });

            return finalIssues;
        },
        getFields ({
            projectId,
            issueTypeId,
            reFetch = false,
        }: {
            projectId: string;
            issueTypeId: string;
            reFetch?: boolean;
        }) {
            if (!isEmpty(this.fields?.[projectId]?.[issueTypeId]) && !reFetch) {
                return Promise.resolve();
            }

            return getJiraFields({ projectId, issueTypeId })
                .then(({ fields }: { fields: unknown[] }) => {
                    this.setFields({ projectId, issueTypeId, fields });
                })
                .catch((error: unknown) => {
                    this.setError({ error });
                    throw error;
                });
        },
        async getInstances ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (this.promises.loadInstances) {
                return this.promises.loadInstances as Promise<unknown>;
            }

            if (this.instances !== null && !reFetch) {
                return Promise.resolve();
            }

            if (this.error && !isJiraConnected(this.error)) {
                this.setError({ error: null });
            }

            const promise = getJiraInstances()
                .then(({ instances }: { instances: Record<string, unknown>[] }) => {
                    this.setInstances({ instances });
                    let jiraStatus = 'connected-configured-enabled';
                    const instance = instances?.[0];
                    if (!instance?.IPaasSolutionInstanceId) {
                        jiraStatus = 'not-connected';
                        this.setError({ error: { message: 'jira integration instance not found' } });
                    } else if (!instance?.Configured) {
                        jiraStatus = 'not-configured';
                        this.setError({ error: { message: 'jira integration not configured' } });
                    } else if (!instance?.Enabled) {
                        jiraStatus = 'disabled';
                        this.setError({ error: { message: 'jira integration not enabled' } });
                    }

                    const pendoGlobal = (window as Window & { pendo: Record<string, unknown> }).pendo;
                    const { visitor, account } = (pendoGlobal.getSerializedMetadata as () => {
                        visitor: unknown;
                        account: Record<string, unknown>;
                    })();
                    (pendoGlobal.updateOptions as (opts: unknown) => void)({
                        visitor,
                        account: {
                            ...account,
                            listenJiraIntegrationStatus: jiraStatus,
                        },
                    });

                    return instances;
                })
                .catch((error: unknown) => {
                    this.setError({ error });
                    throw error;
                })
                .finally(() => {
                    this.setPromise({ promise: null, key: 'loadInstances' });
                });

            this.setPromise({ promise, key: 'loadInstances' });

            return promise;
        },
    },
});

if (module.hot) {
    acceptHMRUpdate(useVocItemsJiraStore, module.hot);
}
